// src/pages/expense/_shared/ExpenseDraftPreviewWindow.jsx
// ✅ 전 사업 확산 [1] 공통화 — 환경/종합 파일럿의 미리보기 팝업(ExpenseEnvTotalDraftPreviewWindow.jsx)
// 을 그대로 일반화한 것. 작성 페이지가 sessionStorage(초기값)+BroadcastChannel(갱신)로
// 넘긴 현재 폼 상태를 그대로 ExpenseProposalPreview로 렌더한다. 이 컴포넌트는 라우트
// element로 직접 쓰이므로(부모가 props를 못 내려줌) 사업별 얇은 래퍼 파일이 자기
// config를 prop으로 넘겨준다.
import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import ExpenseProposalPreview from "../../../components/preview/expense/ExpenseProposalPreview";

export default function ExpenseDraftPreviewWindow({ config }) {
  const { previewStorageKey, previewChannelName } = config;
  const [searchParams] = useSearchParams();
  const autoDownload = searchParams.get("action") === "download";
  const autoPrint = searchParams.get("action") === "print";

  const readStoredData = () => {
    try {
      const raw = sessionStorage.getItem(previewStorageKey);
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  };

  const [data, setData] = useState(readStoredData);

  // ✅ 원본 탭의 실시간 동기화 구독 — 폼을 고치는 동안(300ms 디바운스) 계속 갱신
  // 메시지가 오고, 원본 탭이 페이지를 벗어나거나 저장을 완료하면 "clear"가 와서
  // "작성 중인 문서가 없습니다" 안내로 전환된다.
  useEffect(() => {
    if (!("BroadcastChannel" in window)) return;
    const channel = new BroadcastChannel(previewChannelName);
    channel.onmessage = (e) => {
      if (e.data?.type === "update") setData(e.data.payload);
      if (e.data?.type === "clear") setData(null);
    };
    return () => channel.close();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [previewChannelName]);

  // ✅ "PDF 다운로드" 액션으로 열린 경우, 렌더 완료 후 PreviewPaper 자체의 기존
  // 다운로드 버튼(.download-button)을 프로그램적으로 클릭 — PreviewPaper.jsx를
  // 전혀 수정하지 않고 그대로 재사용하기 위한 방식.
  useEffect(() => {
    if (!autoDownload || !data) return;
    const timer = setTimeout(() => {
      document.querySelector(".download-button")?.click();
    }, 600);
    return () => clearTimeout(timer);
  }, [autoDownload, data]);

  // ✅ "기안 인쇄" 액션으로 열린 경우, 렌더 완료 후 브라우저 인쇄 대화상자를 자동으로
  // 띄운다. 인쇄 대상은 항상 이 창(.preview-paper)뿐 — 작성 페이지에는 문서 DOM이
  // 없어(실시간 동기화를 위해 미리보기가 완전히 이 창으로 분리된 구조) 인쇄가 성립하는
  // 유일한 경로이기도 하다. .preview-header/이 파일이 추가한 상단 바는
  // IncomeProposalPreview.css의 기존 @media print 규칙으로 자동 숨김된다.
  useEffect(() => {
    if (!autoPrint || !data) return;
    const timer = setTimeout(() => window.print(), 600);
    return () => clearTimeout(timer);
  }, [autoPrint, data]);

  useEffect(() => {
    document.title = "기안 문서 미리보기";
  }, []);

  if (!data) {
    return (
      <div style={{ padding: 24, fontSize: 14, color: "#666" }}>
        작성 중인 문서가 없습니다.
      </div>
    );
  }

  return (
    <div className="preview-window-page" style={{ padding: 16 }}>
      {/* ✅ preview-window-page의 padding은 이 창 전용 여백 초기화(기존 @media print
          규칙은 이 페이지의 바깥 래퍼 div를 모르므로 여기서 별도로 0 처리) — 안 하면
          A4 페이지 밖에 16px 여백이 남는다. */}
      <style>{`
        @media print {
          .preview-window-page { padding: 0 !important; }
        }
      `}</style>
      <ExpenseProposalPreview
        templateCode={data.templateCode}
        values={data.values}
        documentNumber={data.documentNumber}
        approvalLine={data.approvalLine}
        hidePreviewButton
        extraActions={
          // ✅ "PDF 미리보기"/"PDF 다운로드"와 같은 줄(.preview-header)에 배치 —
          // PreviewPaper.jsx의 extraActions 슬롯을 통해 렌더되므로 같은 높이 규격
          // (.print-button 공통 클래스)과 인쇄 시 자동 숨김(.preview-header)을 그대로
          // 물려받는다. "PDF 미리보기"는 이미 미리보기 창이라 중복이라 hidePreviewButton
          // 으로 제거.
          <button type="button" onClick={() => window.print()} className="print-button">
            🖨 기안 인쇄
          </button>
        }
      />
    </div>
  );
}
